import type { RpSectionOf } from '@mlm/shared';
import { RP_LIST_CAPS } from '@mlm/shared';
import { cn } from '../../lib/cn';
import { storefrontIcon } from '../../lib/storefront-icons';
import { useRpCtx } from '../context';
import { RpSectionHeader } from '../primitives';
import { rpCols } from '../tokens';

const CARD_STYLE: Record<'plain' | 'card' | 'bordered', string> = {
  plain: '',
  card: 'rounded-lg bg-card p-6 text-card-foreground shadow-card',
  bordered: 'rounded-lg border p-6',
};

/**
 * The "why join" grid — icon or image cards.
 *
 * Icons come from the closed `StorefrontIconKey` allow-list shared with the
 * storefront CMS, so config carries a stable string and never a component
 * reference. An item may instead carry its own uploaded `imageKey`.
 */
export function RpFeatureGrid({ section }: { section: RpSectionOf<'featureGrid'> }) {
  const ctx = useRpCtx();
  const c = section.config;
  // Clamp on read: a page stored before a cap was lowered must still render.
  const items = c.items.slice(0, RP_LIST_CAPS.featureGridItems);
  if (!items.length && !c.heading && !c.subheading) return null;
  const card = CARD_STYLE[c.cardStyle ?? 'card'] ?? CARD_STYLE.card;

  return (
    <div className="space-y-8">
      <RpSectionHeader heading={c.heading} subheading={c.subheading} />
      {items.length ? (
        <div className={cn('grid gap-6', rpCols(c.cols))}>
          {items.map((it, i) => {
            const Icon = it.icon ? storefrontIcon(it.icon) : null;
            return (
              <div key={i} className={cn('space-y-3', card)}>
                {it.imageKey ? (
                  <img src={ctx.imageUrl(it.imageKey)} alt="" className="h-12 w-12 rounded object-cover" />
                ) : Icon ? (
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Icon className="h-6 w-6" aria-hidden />
                  </span>
                ) : null}
                <h3 className="font-heading text-base font-semibold">{it.title}</h3>
                {it.body ? <p className="text-sm opacity-80">{it.body}</p> : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
